/**
 * /dashboard/settings/holidays — Settings → People & HR → Holidays
 * (SOP §8 + §11.4).
 *
 * The full holidays calendar admin lives at /dashboard/holidays (Batch
 * G2 — non-HR users see read-only). This Settings leaf surfaces the
 * "Restore defaults" affordance + a year picker so HR can seed last
 * year's / next year's national calendar idempotently, and links out
 * to the full admin surface for day-by-day edits.
 *
 * Migration note (2026-06-01, Next.js → Vite Phase 3g): direct port of
 * frontend/src/app/(dashboard)/dashboard/settings/holidays/page.tsx.
 * next/link → TanStack Router <Link to=…>.
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { ArrowRight, CalendarDays } from "lucide-react";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { useHolidays } from "@/hooks/use-holidays";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { RestoreDefaultsButton } from "@/components/settings/restore-defaults-button";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/holidays",
)({
  component: SettingsHolidaysRoute,
});

function SettingsHolidaysRoute() {
  const { isHrAdmin } = useHrRoles();
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);

  const years = useMemo(
    () => [currentYear - 1, currentYear, currentYear + 1, currentYear + 2],
    [currentYear],
  );

  // Show a quick count of holidays for the selected year so HR can see
  // whether the calendar is already populated.
  const q = useHolidays({ year });
  const total = q.data?.items?.length ?? 0;

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Holidays</h2>
              <p className="text-sm text-muted-foreground">
                Public-holiday calendar — restore defaults per year, or
                open the full admin to edit day-by-day.
              </p>
            </div>
            <Button asChild variant="outline" className="gap-1.5">
              <Link
                to="/app/$tenantSlug/holidays"
                params={{ tenantSlug }}
              >
                <CalendarDays className="h-4 w-4" />
                Open calendar
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>

          {!isHrAdmin && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only HR managers and company admins can restore default
                holidays. Open the calendar above to view the schedule.
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Restore default calendar
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:max-w-sm">
                <Label htmlFor="holiday-year">Year</Label>
                <Select
                  value={String(year)}
                  onValueChange={(v) => setYear(Number(v))}
                >
                  <SelectTrigger id="holiday-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {total > 0
                    ? `${total} holidays already configured for ${year}.`
                    : `No holidays configured for ${year} yet.`}
                </p>
              </div>
              {isHrAdmin && (
                <RestoreDefaultsButton
                  catalog="holidays"
                  catalogLabel="holidays"
                  holidayYears={[year]}
                  invalidateKeys={[["holidays"]]}
                />
              )}
            </CardContent>
          </Card>
        </div>
      </SettingsShell>
    </>
  );
}
